import React, { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import ActionButton from '@src/components/Buttons/ActionButton';
import AuditLogViewer from '@src/components/AuditLogViewer';
import { FSCollections } from '@src/constants/database';
import { ALLOWED_ROLES, useConfirmDelete, useIsMobile } from '@src/packages/utils';

import type { WorkJourneyForm } from '@src/packages/workJourney/types';

interface WorkJourneyActionsProps {
  record: WorkJourneyForm;
  onEdit: (record: WorkJourneyForm) => void;
  onDelete: (record: WorkJourneyForm) => void | Promise<void>;
  onViewInfo: (record: WorkJourneyForm) => void;
  onOpenMatrix: (record: WorkJourneyForm) => void;
}

const WorkJourneyActions = ({
  record,
  onEdit,
  onDelete,
  onViewInfo,
  onOpenMatrix,
}: WorkJourneyActionsProps) => {
  const intl = useIntl();
  const [auditLogWorkJourneyId, setAuditLogWorkJourneyId] = useState<string | null>(null);

  const { showConfirmDelete, ConfirmDeleteModal } = useConfirmDelete();
  const disabledState = ['pending'].includes(record.state as string);

  const { isMobile } = useIsMobile();

  const handleDeleteClick = useCallback(() => {
    const performDelete = async () => {
      await onDelete(record);
    };

    const journeyName = <strong>{record.name || record.id}</strong>;
    const title = intl.formatMessage({ id: 'workJourney.delete.confirm' }, { name: journeyName });
    const membersCount = record.members?.length ?? 0;
    const message =
      membersCount > 0
        ? intl.formatMessage(
            { id: 'workJourney.delete.membersWarning.content' },
            { quantity: membersCount },
          )
        : intl.formatMessage({ id: 'workJourney.delete.body' });

    showConfirmDelete(message, performDelete, {
      title,
      okDanger: true,
      okText: intl.formatMessage({ id: 'general.confirm.delete' }),
    });
  }, [intl, onDelete, record, showConfirmDelete]);

  const buttons = useMemo(
    () => [
      <ActionButton
        icon="edit"
        tooltipPosition="top"
        disabled={disabledState}
        tooltipText={intl.formatMessage({ id: 'workJourney.actions.edit' })}
        allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE}
        onClick={() => onEdit(record)}
      />,
      <ActionButton
        icon="info"
        tooltipPosition={disabledState ? 'topLeft' : 'top'}
        tooltipText={intl.formatMessage({ id: 'workJourney.actions.info' })}
        allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.GET}
        onClick={() => onViewInfo(record)}
      />,
      <ActionButton
        icon="calendar_view_month"
        tooltipPosition={disabledState ? 'topLeft' : 'top'}
        tooltipText={intl.formatMessage({
          id: 'workJourney.management.matrix.openMonthMatrix',
        })}
        allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE}
        onClick={() => onOpenMatrix(record)}
      />,
      <ActionButton
        icon="delete"
        tooltipPosition="top"
        color="danger"
        disabled={disabledState}
        tooltipText={intl.formatMessage({ id: 'workJourney.actions.delete' })}
        allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.DELETE}
        onClick={handleDeleteClick}
      />,
      <ActionButton
        key="history"
        icon="history"
        tooltipText={intl.formatMessage({ id: 'general.history.label' })}
        allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.AUDIT_LOGS.GET}
        onClick={() => setAuditLogWorkJourneyId(record.id)}
      />,
    ],
    [disabledState, handleDeleteClick, intl, onEdit, onOpenMatrix, onViewInfo, record],
  );
  return (
    <>
      <ActionButton.Container
        maxVisibleButtons={isMobile ? buttons.length : 0}
        overflowVariant="labeled"
        buttons={buttons}
      />
      <AuditLogViewer
        entityType={FSCollections.WORK_JOURNEY_CONFIGS}
        entityId={auditLogWorkJourneyId ?? ''}
        recordLabel={record.name || record.id}
        open={!!auditLogWorkJourneyId}
        onClose={() => setAuditLogWorkJourneyId(null)}
      />
      <ConfirmDeleteModal />
    </>
  );
};

export default WorkJourneyActions;
